import { useMemo } from "react";

import type { TableRow } from "@prequel/shared-components";

import type { ActivityResource } from "@/api/import/activity";
import { useImportActivity } from "@/api/import/activity";
import { useAllProviders } from "@/api/import/providers";
import ImportJobStatusBadge, { IMPORT_JOB_STATUS_OPTIONS } from "@/components/ImportJobStatusBadge";
import ResourceTable, { type FilterField } from "@/components/ResourceTable";
import { getRelativeSubmittedAt } from "@/types";

const COLUMNS = ["Type", "Provider", "Dataset / Stream", "Status", "Total Rows", "Submitted"];

const TYPE_OPTIONS = [
  { value: "extract", label: "Extract" },
  { value: "load", label: "Load" },
];

const TYPE_LABELS: Record<ActivityResource["type"], string> = {
  extract: "Extract",
  load: "Load",
};

// Loads link to their stream's load page; extracts to their dataset's extract page.
const linkToJob = (activity: ActivityResource): string =>
  activity.type === "load" && activity.stream_id
    ? `/import/streams/${activity.stream_id}/loads/${activity.id}`
    : `/import/datasets/${activity.dataset_id}/extracts/${activity.id}`;

const mapActivityToRow = (activity: ActivityResource): TableRow => ({
  id: activity.id,
  type: activity.type,
  to: linkToJob(activity),
  cells: [
    { value: TYPE_LABELS[activity.type] },
    { value: activity.provider_name },
    { value: activity.stream_name ?? activity.dataset_name },
    { value: <ImportJobStatusBadge status={activity.status} /> },
    { value: activity.total_rows.toLocaleString() },
    { value: getRelativeSubmittedAt(activity.submitted_at) },
  ],
});

const ActivityTable = () => {
  const { items: providers, isLoading: providersLoading } = useAllProviders();

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        key: "type",
        label: "Type",
        queryParam: "type",
        radio: true,
        options: TYPE_OPTIONS,
      },
      {
        key: "status",
        label: "Status",
        queryParam: "status",
        radio: true,
        options: IMPORT_JOB_STATUS_OPTIONS,
      },
      {
        key: "provider",
        label: "Provider",
        queryParam: "provider_id",
        loading: providersLoading,
        radio: true,
        options: providers.map((provider) => ({ value: provider.id, label: provider.name })),
      },
    ],
    [providers, providersLoading],
  );

  return (
    <ResourceTable<ActivityResource>
      columns={COLUMNS}
      useQuery={useImportActivity}
      rowMapper={mapActivityToRow}
      emptyMessage="No activity found."
      filterFields={filterFields}
    />
  );
};

export default ActivityTable;
